const REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const TRANSITION_MS = REDUCED_MOTION ? 0 : 1050
export const TRANSITION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'

// How many cards are visible on each side of the center
export const VISIBLE_DEPTH = 2

const HOVER_SCALE_BOOST = 0.06
const HOVER_SCRIM_REDUCTION = 0.18

// Black scrim by distance — cards stay opaque; far cards get darker
const DEPTH_SCRIM = [0, 0.38, 0.58]

const CENTER_TRANSFORM = 'translate(-50%, -50%) translateX(0px) rotateY(0deg) scale(1)'

export const SLIDE_TRANSITION = REDUCED_MOTION
  ? 'none'
  : `transform ${TRANSITION_MS}ms ${TRANSITION_EASING}, opacity ${TRANSITION_MS}ms ${TRANSITION_EASING}`

/** A carousel is one shape throughout: 'portrait' | 'landscape'. */
const CARD_ASPECT = { portrait: 16 / 9, landscape: 9 / 16 }

/** Today's 27rem / 78rem cards. A card never grows past this. */
export const CARD_MAX_PX = { portrait: 432, landscape: 1248 }

/** The widest the content column ever gets, where CARD_MAX_PX was chosen. */
const REFERENCE_COLUMN_PX = 1376

/** Today's flat 1200px perspective, per shape, relative to the card. */
const PERSPECTIVE_FRACTION = { portrait: 2.778, landscape: 0.962 }

/**
 * Fan geometry as fractions of the card width, derived from the pixel offsets
 * this carousel used at its fixed sizes (portrait 432px, landscape 1248px), so
 * a full-width column is pixel-identical and narrower columns scale with it.
 */
const FAN = {
  portrait: {
    x1: 0.833,
    z1: -0.185,
    x2: 1.25,
    z2: -0.394,
    parkStepX: 0.347,
    parkZDrop: 0.162,
    parkZMin: -0.556,
  },
  landscape: {
    x1: 0.673,
    z1: -0.064,
    x2: 0.985,
    z2: -0.136,
    parkStepX: 0.12,
    parkZDrop: 0.056,
    parkZMin: -0.192,
  },
}

export const getCardWidthPx = (shape, columnWidthPx, layout = 'fan') =>
  layout === 'single'
    ? Math.min(CARD_MAX_PX[shape], columnWidthPx * 0.84)
    : Math.min(
        CARD_MAX_PX[shape],
        columnWidthPx * (CARD_MAX_PX[shape] / REFERENCE_COLUMN_PX)
      )

export const getCardHeightPx = (shape, cardW) => cardW * CARD_ASPECT[shape]

export const getPerspectivePx = (shape, cardW) => cardW * PERSPECTIVE_FRACTION[shape]

const clearanceForRotateY = (thetaDeg, cardWidthPx) => {
  const theta = (thetaDeg * Math.PI) / 180
  const halfW = cardWidthPx / 2

  return {
    minX: halfW * (1 + Math.cos(theta)),
    minZ: halfW * Math.sin(theta),
  }
}

// Every visual property recedes as a function of distance from center.
// Z is raised first to clear minZ behind the center plane; X only bumps
// if it is still short of minX after that (preserves horizontal overlap).
const getDepthMetrics = (depth, shape, cardW) => {
  if (depth === 0) return { x: 0, z: 0, rotate: 0, scale: 1 }

  const fan = FAN[shape]

  if (depth === 1) {
    const rotate = 10
    const { minX, minZ } = clearanceForRotateY(rotate, cardW)

    return {
      x: Math.max(fan.x1 * cardW, minX),
      z: Math.min(fan.z1 * cardW, -minZ),
      rotate,
      scale: 0.94,
    }
  }

  if (depth === 2) {
    const rotate = 16
    const { minX, minZ } = clearanceForRotateY(rotate, cardW)

    return {
      x: Math.max(fan.x2 * cardW, minX),
      z: Math.min(fan.z2 * cardW, -minZ),
      rotate,
      scale: 0.85,
    }
  }

  // Off-fan cards park just beyond the deepest visible step so they
  // slide in from outside instead of popping at the center
  const park = getDepthMetrics(2, shape, cardW)

  return {
    x: park.x + (depth - 2) * fan.parkStepX * cardW,
    z: Math.min(park.z - fan.parkZDrop * cardW, fan.parkZMin * cardW),
    rotate: park.rotate,
    scale: 0.8,
  }
}

export const normalizeOffset = (index, activeIndex, count) => {
  let offset = index - activeIndex

  if (offset > count / 2) offset -= count
  if (offset < -count / 2) offset += count

  return offset
}

const getSlideTransform = (offset, isHovered, shape, cardW) => {
  if (offset === 0) return CENTER_TRANSFORM

  const direction = offset < 0 ? -1 : 1
  const { x, z, rotate, scale } = getDepthMetrics(Math.abs(offset), shape, cardW)
  const finalScale = isHovered ? scale + HOVER_SCALE_BOOST : scale

  return `translate(-50%, -50%) translateX(${direction * x}px) translateZ(${z}px) rotateY(${direction * rotate}deg) scale(${finalScale})`
}

export const getScrimOpacity = (depth, isHovered, layout = 'fan') => {
  if (layout === 'single') {
    if (depth === 0) return 0
    if (depth === 1) return 0.45
    return DEPTH_SCRIM[DEPTH_SCRIM.length - 1]
  }

  const base = DEPTH_SCRIM[depth] ?? DEPTH_SCRIM[DEPTH_SCRIM.length - 1]
  if (!isHovered || depth === 0) return base
  return Math.max(0, base - HOVER_SCRIM_REDUCTION)
}

export const getSlideStyle = (
  offset,
  stackOffset,
  isHovered,
  isTransitioning,
  shape,
  cardW,
  layout = 'fan'
) => {
  if (layout === 'single') {
    if (offset === 0) {
      return {
        transform: CENTER_TRANSFORM,
        opacity: 1,
        zIndex: 'var(--z-dock)',
        pointerEvents: undefined,
      }
    }

    const direction = offset < 0 ? -1 : 1
    const isPeek = Math.abs(offset) === 1

    if (isPeek) {
      return {
        transform: `translate(-50%, -50%) translateX(${direction * cardW * 0.92}px) scale(0.92)`,
        opacity: 1,
        zIndex: 'calc(var(--z-dock) - 1)',
        pointerEvents: 'auto',
      }
    }

    return {
      transform: `translate(-50%, -50%) translateX(${direction * (cardW + 24)}px)`,
      opacity: 0,
      zIndex: `calc(var(--z-dock) - ${Math.min(Math.abs(offset), 20)})`,
      pointerEvents: 'none',
    }
  }

  const depth = Math.abs(offset)
  const isHiddenExtra = depth > VISIBLE_DEPTH
  const sortDepth = isTransitioning ? Math.abs(stackOffset) : depth

  return {
    transform: getSlideTransform(offset, isHovered, shape, cardW),
    // Visible cards stay fully solid; only park off-fan cards
    opacity: isHiddenExtra ? 0 : 1,
    // Center on top; each step back one z-index lower
    zIndex: `calc(var(--z-dock) - ${Math.min(sortDepth, 20)})`,
    pointerEvents: isHiddenExtra ? 'none' : undefined,
  }
}

/** Fan: stack order mid-transition. Single: final offset from navigateTo. */
export const getCarouselVisualDepth = (
  offset,
  stackOffset,
  isTransitioning,
  layout = 'fan'
) =>
  layout === 'single' || !isTransitioning ? Math.abs(offset) : Math.abs(stackOffset)
